import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import Pusher from 'pusher-js';
import { jwtDecode } from 'jwt-decode';
import { IPayloadToken } from 'src/app/interfaces/Global/PayloadToken';
import { PhotoStorageService } from '../General/PhotoStorage/photo-storage.service';
import { IPhotoAnalysisNotification } from 'src/app/interfaces/Outputs/PhotoAnalysis';

@Injectable({
  providedIn: 'root',
})
export class PhotoAnalysisService {
  private pusher: Pusher | undefined;
  /**
   * Se recuerda el canal al suscribir para que desuscribirse NO dependa del JWT.
   *
   * Importa por el orden real de salida: al terminar o expirar la inspección el JWT puede haberse
   * limpiado antes, y entonces un `unsubscribe` que lo necesita para recalcular el nombre del canal
   * no se ejecuta nunca y la conexión queda viva.
   */
  private subscribedChannel: string | undefined;
  photoChannelPrefix = environment.push.channelPrefix;
  photoAnalysisEvent = environment.push.photoAnalysisEvent;
  authEndpoint = `${environment.GATEWAY_HOST}/api/push/auth`;

  constructor(
    private readonly photoStorageService: PhotoStorageService
  ) {
  }

  private intiializePusher(jwt: string) {
    if (this.pusher) return;
    this.pusher = new Pusher(environment.push.pusherAppKey, {
      cluster: environment.push.pusherCluster,
      channelAuthorization: {
        endpoint: this.authEndpoint,
        transport: 'ajax',
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      },
    });

    this.pusher.connection.bind('connected', () => {
      console.log('✅ Pusher connected');
    });

    this.pusher.connection.bind('error', (err: any) => {
      console.error('❌ Pusher connection error:', err);
    });
  }

  subscribeToPhotoAnalysis(jwt: string) {
    const inspectionId = Number(jwtDecode<IPayloadToken>(jwt).inspection_id);
    this.intiializePusher(jwt);
    if (!this.pusher) {
      console.error('⚠️ Pusher no inicializado al suscribirse');
      return;
    }
    const channelName = `${this.photoChannelPrefix}.${inspectionId}`;
    if (this.subscribedChannel === channelName) {
      return;
    }
    const channel = this.pusher.subscribe(channelName);
    this.subscribedChannel = channelName;

    channel.bind(this.photoAnalysisEvent, (data: any) => {
        this.handlePhotoAnalysisResults(data);
    });

    channel.bind('pusher:subscription_succeeded', () => {
      console.log(`✅ Suscrito al canal ${channelName}`);
    });

    channel.bind('pusher:subscription_error', (status: any) => {
      console.error(`❌ Error al suscribirse al canal ${channelName}:`, status);
    });
  }

  /** Idempotente: se la puede llamar al salir del flujo y otra vez al expirar sin efecto extra. */
  unsubscribeFromPhotoAnalysis() {
    if (!this.pusher) {
      return;
    }
    if (this.subscribedChannel) {
      console.log(`Desuscribiéndose del canal: ${this.subscribedChannel}`);
      this.pusher.unsubscribe(this.subscribedChannel);
    }
    // `unsubscribe` deja el socket abierto: sin `disconnect` la conexión sobrevive a la inspección.
    this.pusher.disconnect();
    this.pusher = undefined;
    this.subscribedChannel = undefined;
  }

  private handlePhotoAnalysisResults(data: IPhotoAnalysisNotification) {
    this.photoStorageService.handlePhotoAnalysisResults(data);
  }
}
